package com.schoolconnect.app.feature.reports

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.*
import com.schoolconnect.app.designsystem.list.*
import com.schoolconnect.app.feature.attendance.ParentAttendanceChild
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.health_chart
import ischoolconnect.sharedui.generated.resources.reports
import ischoolconnect.sharedui.generated.resources.stat_users
import ischoolconnect.sharedui.generated.resources.user
import org.jetbrains.compose.resources.painterResource

private data class ExamDemo(val name: String, val period: String, val marks: String, val percentage: String, val grade: String, val rank: String, val starts: Int, val ends: Int)

@Composable
fun ParentStudentPerformanceReport(
    child: ParentAttendanceChild?,
    modifier: Modifier = Modifier
) {
    var fromDate by remember { mutableStateOf("01 Jul 2026") }
    var toDate by remember { mutableStateOf("30 Jul 2026") }
    var appliedFromDate by remember { mutableStateOf("01 Jul 2026") }
    var appliedToDate by remember { mutableStateOf("30 Jul 2026") }
    var fullReport by remember { mutableStateOf(false) }
    var feedback by remember { mutableStateOf("") }
    var error by remember { mutableStateOf("") }
    val exams = remember(child?.id, appliedFromDate, appliedToDate) {
        val start = dateKey(appliedFromDate)
        val end = dateKey(appliedToDate)
        listOf(
            ExamDemo("Unit Test 2", "27–30 Jul 2026", "461 / 500", "92.2%", "A+", "2", 20260727, 20260730),
            ExamDemo("Mid-Term Examination", "14–25 Jul 2026", "448 / 500", "89.6%", "A", "3", 20260714, 20260725),
            ExamDemo("Unit Test 1", "08–12 Jul 2026", "432 / 500", "86.4%", "A", "5", 20260708, 20260712)
        ).filter { start != null && end != null && it.ends >= start && it.starts <= end }
    }
    val childName = child?.name?.takeIf(String::isNotBlank) ?: "Aarav Kumar"
    val className = child?.classLabel?.takeIf(String::isNotBlank) ?: "Class 5-A"
    Column(modifier.fillMaxSize().verticalScroll(rememberScrollState()).background(AppBackground).padding(bottom = 24.dp), verticalArrangement = Arrangement.spacedBy(18.dp)) {
        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text("Reports & Analytics", color = AppPageHeader, fontSize = 28.sp, fontWeight = FontWeight.SemiBold)
            Text("View your child's attendance and academic performance.", color = AppPageSubtitle, fontSize = 14.sp)
        }
        AppCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().padding(20.dp), verticalArrangement = Arrangement.spacedBy(14.dp)) {
                Text("Report Configuration", color = AppNavy, fontSize = 18.sp, fontWeight = FontWeight.SemiBold)
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.dp), verticalAlignment = Alignment.Bottom) {
                    Column(Modifier.weight(1.4f), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        Text("Report Type", color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                        AppLabeledDropdownField(label = "", value = "Student Performance Report", options = listOf("Student Performance Report"), onSelected = {}, includeAllOption = false, enabled = false, modifier = Modifier.fillMaxWidth())
                    }
                    DateInput("From Date", fromDate, { fromDate = it; error = "" }, Modifier.weight(1f))
                    DateInput("To Date", toDate, { toDate = it; error = "" }, Modifier.weight(1f))
                }
                if (error.isNotBlank()) Text(error, color = AppDanger, fontSize = 12.sp)
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End, verticalAlignment = Alignment.CenterVertically) {
                    TextButton(onClick = { fromDate = "01 Jul 2026"; toDate = "30 Jul 2026"; appliedFromDate = fromDate; appliedToDate = toDate; fullReport = false; error = "" }) { Text("Reset", color = AppPrimary) }
                    Spacer(Modifier.width(8.dp))
                    AppButton(onClick = {
                        val start = dateKey(fromDate)
                        val end = dateKey(toDate)
                        when { start == null || end == null -> error = "Enter both dates using DD MMM YYYY."; start > end -> error = "From Date must be on or before To Date."; else -> { appliedFromDate = fromDate; appliedToDate = toDate; fullReport = false; error = ""; feedback = "Demo report refreshed from local sample data." } }
                    }) { Text("Run Report") }
                }
            }
        }
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text("Attendance Summary", color = AppNavy, fontSize = 20.sp, fontWeight = FontWeight.SemiBold)
            Row(Modifier.fillMaxWidth()) {
                AdminListSummaryCards(items = listOf(
                    summary("Working Days", "22", "School days in period", "calendar"),
                    summary("Present Days", "20", "Attendance equivalent", "health_chart"),
                    summary("Absent Days", "2", "Full and partial days", "user"),
                    summary("Attendance", "90.9%", "Good", "reports", AppSuccess)
                ))
            }
        }
        AppCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().padding(20.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("Report Preview", color = AppNavy, fontSize = 18.sp, fontWeight = FontWeight.SemiBold)
                Text("Student Performance Report — $childName", color = AppNavy, fontSize = 16.sp, fontWeight = FontWeight.SemiBold)
                Text("$fromDate–$toDate   ·   $className", color = AppMuted, fontSize = 14.sp)
                Text("Exam Summary", color = AppNavy, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
                Text("Published exams within the selected date range", color = AppMuted, fontSize = 12.sp)
                if (exams.isEmpty()) {
                    AdminListEmptyState(title = "No published exams found for the selected period.", subtitle = "Attendance summary remains available for this reporting period.")
                } else {
                    ExamTable(exams.take(if (fullReport) exams.size else 5))
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                        Text("Showing ${exams.size} published exams", color = AppMuted, fontSize = 12.sp)
                        Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                            AppOutlinedButton(onClick = { fullReport = !fullReport }) { Text(if (fullReport) "Show Preview" else "View Full Report") }
                            AppOutlinedButton(onClick = { feedback = "Demo Excel export is ready to connect; no file was generated." }) { Text("Export to Excel") }
                            AppButton(onClick = { feedback = "Demo PDF export is ready to connect; no file was generated." }) { Text("Download PDF") }
                        }
                    }
                }
                if (feedback.isNotBlank()) Text(feedback, color = AppMuted, fontSize = 12.sp)
                Text("Sample data only · child context follows the selection in the header", color = AppMuted, fontSize = 12.sp)
            }
        }
    }
}

private fun dateKey(value: String): Int? {
    val parts = value.trim().split(Regex("\\s+"))
    if (parts.size != 3) return null
    val day = parts[0].toIntOrNull() ?: return null
    val month = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec").indexOfFirst { it.equals(parts[1], ignoreCase = true) } + 1
    val year = parts[2].toIntOrNull() ?: return null
    if (month !in 1..12 || day !in 1..31 || year !in 2000..2100) return null
    return year * 10000 + month * 100 + day
}

@Composable private fun summary(title: String, value: String, subtitle: String, icon: String, subtitleTint: Color = AppMuted): AdminListSummaryCardItem {
    val painter = when (icon) {
        "health_chart" -> painterResource(Res.drawable.health_chart)
        "user" -> painterResource(Res.drawable.user)
        "reports" -> painterResource(Res.drawable.reports)
        else -> painterResource(Res.drawable.stat_users)
    }
    return AdminListSummaryCardItem(title, value, subtitle, painter, AppPrimary, AppPrimarySoft, subtitleTint)
}

@Composable private fun DateInput(label: String, value: String, onChange: (String) -> Unit, modifier: Modifier = Modifier) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(label, color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
        AppDialogDateField(value, onChange, Modifier.fillMaxWidth(), placeholder = "DD MMM YYYY", fieldHeight = 40.dp)
    }
}

@Composable private fun ExamTable(exams: List<ExamDemo>) {
    val headers = listOf("Exam Name", "Exam Period", "Marks", "Percentage", "Grade", "Rank")
    Column(Modifier.fillMaxWidth().background(Color.White, RoundedCornerShape(8.dp))) {
        Row(Modifier.fillMaxWidth().background(Color(0xFFF8FAFC)).padding(vertical = 11.dp)) { headers.forEach { Text(it, Modifier.weight(1f), color = AppNavy, fontSize = 12.sp, fontWeight = FontWeight.SemiBold, textAlign = TextAlign.Center) } }
        exams.forEach { exam ->
            HorizontalDivider(color = AppBorder)
            Row(Modifier.fillMaxWidth().padding(vertical = 11.dp), verticalAlignment = Alignment.CenterVertically) {
                listOf(exam.name, exam.period, exam.marks, exam.percentage, exam.grade, exam.rank).forEachIndexed { index, value ->
                    Text(value, Modifier.weight(1f).padding(horizontal = 4.dp), color = when(index) { 3 -> AppPrimary; 4 -> AppSuccess; else -> AppNavy }, fontSize = 12.sp, textAlign = TextAlign.Center)
                }
            }
        }
    }
}
